// Facebook Pixel Tracking Service - FBC/FBP Parameter Management & Persistence
// Extracted from metaTracking.js for better separation of concerns

import { debugLog, debugWarn, debugError } from '../debugUtils';

// Session storage keys for Facebook tracking persistence
const FBC_SESSION_KEY = 'swts_fbc_session';
const FBP_SESSION_KEY = 'swts_fbp_session';
const FBC_EXPIRY_KEY = 'swts_fbc_expiry';
const FBP_EXPIRY_KEY = 'swts_fbp_expiry';
// CRITICAL: Store raw fbclid separately to prevent URL decoding corruption
const RAW_FBCLID_KEY = 'swts_raw_fbclid';
const RAW_FBCLID_EXPIRY_KEY = 'swts_raw_fbclid_expiry';

// Timing constants for FBP improvements
const FBP_RETRY_ATTEMPTS = 4; // Number of retry attempts for _fbp cookie
const FBP_RETRY_DELAYS =
  process.env.NODE_ENV === 'test' ? [10, 20, 30, 40] : [500, 1000, 2000, 3000]; // Progressive delays in ms
const FBP_PIXEL_READY_TIMEOUT = process.env.NODE_ENV === 'test' ? 100 : 5000; // Max time to wait for pixel readiness

// trackEventFunction removed - no longer needed for meta_ events

/**
 * Helper function to wait for Meta Pixel to be ready and initialized
 */
const waitForMetaPixelReady = async (maxWaitMs = FBP_PIXEL_READY_TIMEOUT) => {
  const startTime = Date.now();

  return new Promise(resolve => {
    const checkPixelReady = () => {
      const elapsed = Date.now() - startTime;

      // Check if pixel is loaded and initialized
      const pixelReady =
        typeof window !== 'undefined' &&
        typeof window.fbq === 'function' &&
        window.fbq.loaded === true;

      if (pixelReady) {
        debugLog('✅ Meta Pixel detected as ready and initialized');
        resolve({ ready: true, elapsed });
        return;
      }

      // Timeout reached
      if (elapsed >= maxWaitMs) {
        const reason =
          typeof window === 'undefined'
            ? 'server-side'
            : typeof window.fbq === 'undefined'
              ? 'fbq not loaded'
              : 'fbq not initialized';
        debugLog(
          `⏰ Meta Pixel readiness timeout after ${elapsed}ms (${reason})`
        );
        resolve({ ready: false, elapsed, reason });
        return;
      }

      // Continue checking (use shorter interval in tests)
      setTimeout(checkPixelReady, process.env.NODE_ENV === 'test' ? 10 : 100);
    };

    checkPixelReady();
  });
};

/**
 * CRITICAL: Extract raw fbclid without ANY URL decoding to preserve Meta's exact value
 * Meta's official guidance: "Do not modify the fbclid value retrieved from fbclid query parameter"
 */
const extractRawFbclid = (url = null) => {
  try {
    const targetUrl =
      url || (typeof window !== 'undefined' ? window.location.href : null);
    if (!targetUrl) return null;

    // Method 1: Manual extraction without URL decoding (CRITICAL for Meta attribution)
    const questionMarkIndex = targetUrl.indexOf('?');
    if (questionMarkIndex === -1) return null;

    const searchParams = targetUrl.substring(questionMarkIndex + 1);
    const params = searchParams.split('&');

    for (const param of params) {
      if (param.startsWith('fbclid=')) {
        const rawFbclid = param.substring(7); // Extract without ANY decoding

        // SECURITY: Basic validation without modification
        if (rawFbclid && rawFbclid.length > 1 && rawFbclid.length < 500) {
          debugLog(
            `✅ Raw fbclid extracted: ${rawFbclid.substring(0, 15)}... (length: ${rawFbclid.length})`
          );

          // Store the raw fbclid immediately to prevent loss
          storeRawFbclid(rawFbclid);

          // Basic validation - keep fbclid extraction simple
          if (rawFbclid.length <= 6) {
            debugWarn('⚠️ Unusually short fbclid detected', {
              fbclid_length: rawFbclid.length,
            });
          }

          return rawFbclid;
        }
      }
    }

    return null;
  } catch (error) {
    debugLog('Error extracting raw fbclid:', error);
    return null;
  }
};

/**
 * Store raw fbclid with high priority to prevent corruption
 */
const storeRawFbclid = async rawFbclid => {
  if (!rawFbclid || typeof window === 'undefined') return;

  try {
    const { storeUserData } = await import('../userDataPersistence');
    const expirationTime = Date.now() + 7 * 24 * 60 * 60 * 1000; // 7 days

    await storeUserData({
      [RAW_FBCLID_KEY]: rawFbclid,
      [RAW_FBCLID_EXPIRY_KEY]: expirationTime.toString(),
    });

    debugLog('🔒 Raw fbclid stored securely to prevent corruption');
  } catch (error) {
    debugLog('Failed to store raw fbclid:', error);
  }
};

/**
 * Retrieve stored raw fbclid with expiration check
 */
const getStoredRawFbclid = () => {
  try {
    const { getUserData } = require('../userDataPersistence');
    const userData = getUserData();

    const rawFbclid = userData?.[RAW_FBCLID_KEY];
    const expiryTime = userData?.[RAW_FBCLID_EXPIRY_KEY];

    if (rawFbclid && expiryTime) {
      if (Date.now() < parseInt(expiryTime)) {
        debugLog('📦 Retrieved stored raw fbclid (still valid)');

        return rawFbclid;
      } else {
        debugLog('⏰ Stored raw fbclid expired');
        // Clean up expired data
        import('../userDataPersistence').then(
          async ({ removeUserDataFields }) => {
            await removeUserDataFields([RAW_FBCLID_KEY, RAW_FBCLID_EXPIRY_KEY]);
          }
        );
      }
    }

    return null;
  } catch (error) {
    debugLog('Error retrieving stored raw fbclid:', error);
    return null;
  }
};

/**
 * Enhanced function to get FBC with comprehensive retry and fallback strategies
 */
/**
 * Enhanced FBC retrieval with the same success strategy as FBP
 * Wait for Meta Pixel + progressive retries + comprehensive fallbacks
 */
export const getEnhancedFbc = async () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { fbc: null, fbcSource: 'server-side' };
  }

  const getCookie = name => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const FBC_EXPIRATION = 7 * 24 * 60 * 60 * 1000; // 7 days (Meta's click attribution window)

  // Import userDataPersistence functions
  let storeUserData, getUserData, removeUserDataFields;
  try {
    const userDataModule = await import('../userDataPersistence');
    storeUserData = userDataModule.storeUserData;
    getUserData = userDataModule.getUserData;
    removeUserDataFields = userDataModule.removeUserDataFields;
  } catch (error) {
    debugLog('Failed to load userDataPersistence:', error);
    return { fbc: null, fbcSource: 'persistence_error' };
  }

  const getStorageValue = (key, expirationMs = null) => {
    try {
      const userData = getUserData();
      const value = userData?.[key];

      // Check expiration if specified
      if (value && expirationMs) {
        const expiryKey = key + '_expiry';
        const expiryTime = userData?.[expiryKey];

        if (expiryTime && Date.now() > parseInt(expiryTime)) {
          // Value has expired, clean it up
          removeUserDataFields([key, expiryKey]).catch(() => {});
          debugLog(`🕐 Expired and removed ${key} from storage`);
          return null;
        }
      }

      return value || null;
    } catch (error) {
      debugLog(`Storage access error for ${key}:`, error);
      return null;
    }
  };

  const setStorageValue = async (key, value, expirationMs = null) => {
    const expiryTime = expirationMs ? Date.now() + expirationMs : null;

    try {
      const dataToStore = { [key]: value };

      // Set expiration if specified
      if (expiryTime) {
        dataToStore[key + '_expiry'] = expiryTime.toString();
      }

      await storeUserData(dataToStore);
      debugLog(
        `Saved ${key} to userDataPersistence${expiryTime ? ` (expires: ${new Date(expiryTime).toISOString()})` : ''}`
      );
      return true;
    } catch (error) {
      debugLog(`Failed to save ${key} to userDataPersistence:`, error);
      return false;
    }
  };

  // === Strategy 1: Check existing Meta native _fbc cookie ===
  let fbc = getCookie('_fbc');
  if (fbc) {
    debugLog('✅ Found existing Meta native _fbc cookie');
    // Store for future use
    await setStorageValue(FBC_SESSION_KEY, fbc, FBC_EXPIRATION);
    return { fbc, fbcSource: 'Meta native _fbc (existing)' };
  }

  // === Strategy 2: Wait for Meta Pixel to load then extract fbclid ===
  debugLog(
    '🔄 Meta native _fbc not found, waiting for Meta Pixel to initialize...'
  );

  const pixelReadiness = await waitForMetaPixelReady();

  if (pixelReadiness.ready) {
    // Try progressive retry strategy with delays - same as FBP
    for (let attempt = 0; attempt < FBP_RETRY_ATTEMPTS; attempt++) {
      const delay =
        FBP_RETRY_DELAYS[attempt] ||
        FBP_RETRY_DELAYS[FBP_RETRY_DELAYS.length - 1];

      if (attempt > 0) {
        debugLog(
          `⏳ FBC retry attempt ${attempt + 1}/${FBP_RETRY_ATTEMPTS} - waiting ${delay}ms...`
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      // First check if Meta set _fbc cookie after pixel loaded
      fbc = getCookie('_fbc');
      if (fbc) {
        debugLog(
          `✅ Meta Pixel set _fbc cookie on attempt ${attempt + 1}/${FBP_RETRY_ATTEMPTS}`
        );
        await setStorageValue(FBC_SESSION_KEY, fbc, FBC_EXPIRATION);
        return { fbc, fbcSource: `Meta native _fbc (retry ${attempt + 1})` };
      }

      // If no _fbc cookie, try to extract fbclid and generate FBC
      try {
        let rawFbclid = extractRawFbclid();

        // Fallback to stored raw fbclid if URL extraction fails
        if (!rawFbclid) {
          rawFbclid = getStoredRawFbclid();
          if (rawFbclid) {
            debugLog('📦 Using stored raw fbclid as fallback');
          }
        }

        if (rawFbclid) {
          // Use navigationStart as first observed time (Meta's guidance)
          let firstObservedTime;
          if (
            typeof window !== 'undefined' &&
            window.performance &&
            window.performance.timing &&
            window.performance.timing.navigationStart
          ) {
            firstObservedTime = window.performance.timing.navigationStart;
            debugLog(
              `Using navigationStart (${firstObservedTime}) as first observed time`
            );
          } else {
            firstObservedTime = Date.now();
            debugLog(
              `Performance Timing API not available, using current timestamp`
            );
          }

          // Generate fbc with Meta's recommended format
          fbc = `fb.1.${firstObservedTime}.${rawFbclid}`;
          debugLog(
            `✅ Generated FBC from fbclid on attempt ${attempt + 1}/${FBP_RETRY_ATTEMPTS}`
          );

          // CRITICAL: Final validation - ensure fbclid wasn't modified
          const generatedFbclidPart = fbc.split('.')[3];
          if (generatedFbclidPart !== rawFbclid) {
            debugWarn(
              '🚨 CRITICAL: fbclid was modified during FBC generation!',
              {
                original: rawFbclid,
                generated: generatedFbclidPart,
                fbc: fbc,
              }
            );
          }

          // Store for future use
          await setStorageValue(FBC_SESSION_KEY, fbc, FBC_EXPIRATION);
          return { fbc, fbcSource: `URL fbclid (retry ${attempt + 1})` };
        }
      } catch (error) {
        debugLog(`Error extracting fbclid on attempt ${attempt + 1}:`, error);
      }
    }

    debugLog(
      `⚠️ Meta Pixel ready but failed to get FBC after ${FBP_RETRY_ATTEMPTS} attempts`
    );
  } else {
    debugLog(
      `⚠️ Meta Pixel not ready: ${pixelReadiness.reason} (waited ${pixelReadiness.elapsed}ms)`
    );
  }

  // === Strategy 3: Check stored FBC as backup ===
  const storedFbc = getStorageValue(FBC_SESSION_KEY, FBC_EXPIRATION);
  if (storedFbc) {
    debugLog('✅ Using stored FBC as backup');
    return { fbc: storedFbc, fbcSource: 'FBC (stored backup)' };
  }

  // === Strategy 4: Final attempt - immediate fbclid extraction ===
  debugLog('🔄 Final attempt: immediate fbclid extraction without waiting...');
  try {
    let rawFbclid = extractRawFbclid();

    if (!rawFbclid) {
      rawFbclid = getStoredRawFbclid();
    }

    if (rawFbclid) {
      const firstObservedTime =
        window.performance?.timing?.navigationStart || Date.now();
      fbc = `fb.1.${firstObservedTime}.${rawFbclid}`;
      debugLog('✅ Generated FBC from immediate fbclid extraction');

      await setStorageValue(FBC_SESSION_KEY, fbc, FBC_EXPIRATION);
      return { fbc, fbcSource: 'URL fbclid (immediate)' };
    }
  } catch (error) {
    debugLog('Final fbclid extraction failed:', error);
  }

  // === Strategy 5: Complete failure - transparent about no FBC ===
  debugLog('🚨 All FBC strategies failed - no FBC available');
  debugLog(
    '📊 Event will proceed without FBC, relying on other matching signals (email, IP, user agent, FBP)'
  );

  return { fbc: null, fbcSource: 'All FBC strategies failed' };
};

export const getEnhancedFbp = async () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { fbp: null, fbpSource: 'server-side' };
  }

  const getCookie = name => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
  };

  const FBP_EXPIRATION = 90 * 24 * 60 * 60 * 1000; // 90 days

  // Import userDataPersistence functions
  let getUserData, removeUserDataFields;
  try {
    const userDataModule = await import('../userDataPersistence');
    getUserData = userDataModule.getUserData;
    removeUserDataFields = userDataModule.removeUserDataFields;
  } catch (error) {
    debugLog('Failed to load userDataPersistence:', error);
    return { fbp: null, fbpSource: 'persistence_error' };
  }

  const getStorageValue = (key, expirationMs = null) => {
    try {
      const userData = getUserData();
      const value = userData?.[key];

      // Check expiration if specified
      if (value && expirationMs) {
        const expiryKey = key + '_expiry';
        const expiryTime = userData?.[expiryKey];

        if (expiryTime && Date.now() > parseInt(expiryTime)) {
          // Value has expired, clean it up
          removeUserDataFields([key, expiryKey]).catch(() => {});
          debugLog(`🕐 Expired and removed ${key} from storage`);
          return null;
        }
      }

      return value || null;
    } catch (error) {
      debugLog(`Storage access error for ${key}:`, error);
      return null;
    }
  };

  const setStorageValue = async (key, value, expirationMs = null) => {
    const expiryTime = expirationMs ? Date.now() + expirationMs : null;

    // Use userDataPersistence (which handles all fallbacks internally)
    try {
      const { storeUserData } = await import('../userDataPersistence');
      const dataToStore = { [key]: value };

      // Set expiration if specified
      if (expiryTime) {
        dataToStore[key + '_expiry'] = expiryTime.toString();
      }

      await storeUserData(dataToStore);
      debugLog(
        `Saved ${key} to userDataPersistence${expiryTime ? ` (expires: ${new Date(expiryTime).toISOString()})` : ''}`
      );
      return true;
    } catch (error) {
      debugLog(`Failed to save ${key} to userDataPersistence:`, error);
      return false;
    }
  };

  // === Strategy 1: Check existing Meta native _fbp cookie ===
  let fbp = getCookie('_fbp');
  if (fbp) {
    debugLog('✅ Found existing Meta native _fbp cookie');
    // Store for future use
    await setStorageValue(FBP_SESSION_KEY, fbp);
    return { fbp, fbpSource: 'Meta native _fbp (existing)' };
  }

  // === Strategy 2: Wait for Meta Pixel to load and set _fbp cookie ===
  debugLog(
    '🔄 Meta native _fbp not found, waiting for Meta Pixel to initialize...'
  );

  const pixelReadiness = await waitForMetaPixelReady();

  if (pixelReadiness.ready) {
    // Try progressive retry strategy with delays
    for (let attempt = 0; attempt < FBP_RETRY_ATTEMPTS; attempt++) {
      const delay =
        FBP_RETRY_DELAYS[attempt] ||
        FBP_RETRY_DELAYS[FBP_RETRY_DELAYS.length - 1];

      if (attempt > 0) {
        debugLog(
          `⏳ FBP retry attempt ${attempt + 1}/${FBP_RETRY_ATTEMPTS} - waiting ${delay}ms...`
        );
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      fbp = getCookie('_fbp');
      if (fbp) {
        debugLog(
          `✅ Meta Pixel set _fbp cookie on attempt ${attempt + 1}/${FBP_RETRY_ATTEMPTS}`
        );
        // Store for future use
        await setStorageValue(FBP_SESSION_KEY, fbp);
        return { fbp, fbpSource: `Meta native _fbp (retry ${attempt + 1})` };
      }
    }

    debugLog(
      `⚠️ Meta Pixel ready but failed to set _fbp after ${FBP_RETRY_ATTEMPTS} attempts`
    );
  } else {
    debugLog(
      `⚠️ Meta Pixel not ready: ${pixelReadiness.reason} (waited ${pixelReadiness.elapsed}ms)`
    );
  }

  // === Strategy 3: Check stored Meta native _fbp as backup ===
  const storedFbp = getStorageValue(FBP_SESSION_KEY, FBP_EXPIRATION);
  if (storedFbp) {
    debugLog('✅ Using stored Meta native _fbp as backup');

    return { fbp: storedFbp, fbpSource: 'Meta native _fbp (stored backup)' };
  }

  // === Strategy 4: Generate fallback FBP following Meta's format ===
  debugLog(
    '🔄 Meta Pixel failed to set _fbp - generating fallback FBP to ensure 100% coverage'
  );

  // Generate FBP following Meta's exact format: fb.subdomainIndex.creationTime.randomnumber
  try {
    // Calculate subdomain index based on Meta's specification
    const calculateSubdomainIndex = () => {
      if (typeof window === 'undefined' || !window.location) return 1;

      const hostname = window.location.hostname;
      const parts = hostname.split('.');

      // Meta's specification: 'com' = 0, 'example.com' = 1, 'www.example.com' = 2
      if (parts.length === 1 || hostname === 'localhost') return 0; // Single domain or localhost
      if (parts.length === 2) return 1; // example.com
      return 2; // www.example.com or subdomain.example.com
    };

    // Generate creation timestamp (current time in milliseconds)
    const creationTime = Date.now();

    // Generate random number (Meta Pixel typically uses large numeric strings)
    // Match Meta's pattern: typically 13-16 digit numbers
    const randomNumber =
      Math.floor(Math.random() * 9000000000000) + 1000000000000;

    // Construct FBP following Meta's format
    const subdomainIndex = calculateSubdomainIndex();
    const generatedFbp = `fb.${subdomainIndex}.${creationTime}.${randomNumber}`;

    debugLog(`✅ Generated fallback FBP: ${generatedFbp}`);

    // Set as cookie for future use (90 days expiry to match Meta's standard)
    const setCookie = (name, value, days) => {
      const expires = new Date(
        Date.now() + days * 24 * 60 * 60 * 1000
      ).toUTCString();
      document.cookie = `${name}=${value}; expires=${expires}; path=/; Secure; SameSite=Lax`;
    };

    setCookie('_fbp', generatedFbp, 90);
    debugLog('📝 Set generated FBP as cookie for future use');

    // Store in userDataPersistence for reliability
    await setStorageValue(FBP_SESSION_KEY, generatedFbp, FBP_EXPIRATION);

    return { fbp: generatedFbp, fbpSource: 'Generated fallback _fbp' };
  } catch (error) {
    debugError('❌ Failed to generate fallback FBP:', error);

    return { fbp: null, fbpSource: 'Fallback generation failed' };
  }
};

/**
 * Helper function to manage Facebook tracking parameter persistence
 */
export const getFacebookTrackingParams = async () => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return { fbc: null, fbp: null };
  }

  // Get enhanced FBC and FBP
  const { fbc, fbcSource } = await getEnhancedFbc();
  const { fbp, fbpSource } = await getEnhancedFbp();

  // ENHANCED: Track potential encoding corruption with more detail
  if (
    fbc &&
    (fbc.includes('%') ||
      fbc.includes('+') ||
      fbc.includes(' ') ||
      !fbc.startsWith('fb.'))
  ) {
    debugWarn('🚨 CRITICAL: FBC corruption detected!', {
      fbc_preview: fbc.substring(0, 50) + '...',
      has_url_encoding: fbc.includes('%'),
      has_spaces: fbc.includes(' '),
      has_plus: fbc.includes('+'),
      valid_format: fbc.startsWith('fb.'),
      source: fbcSource,
    });
  }

  // Basic FBC validation - keep simple
  if (fbc && !fbc.startsWith('fb.')) {
    debugWarn('⚠️ Invalid FBC format detected', {
      fbc_preview: fbc.substring(0, 20) + '...',
      source: fbcSource,
    });
  }

  if (
    fbp &&
    (fbp.includes('%') ||
      fbp.includes('+') ||
      fbp.includes(' ') ||
      !fbp.startsWith('fb.'))
  ) {
  }

  // Basic FBP validation - keep simple
  if (fbp && !fbp.startsWith('fb.')) {
    debugWarn('⚠️ Invalid FBP format detected', {
      fbp_preview: fbp.substring(0, 20) + '...',
      source: fbpSource,
    });
  }

  return {
    fbc: fbc, // Facebook Click ID - consistent across session
    fbp: fbp, // Facebook Browser ID - now with comprehensive fallback strategies
  };
};

/**
 * Helper function to clear Facebook tracking session data (for privacy/debugging)
 */
export const clearFacebookTrackingSession = async () => {
  if (typeof window === 'undefined') {
    return;
  }

  // Clear from userDataPersistence (handles all storage fallbacks internally)
  try {
    const { removeUserDataFields } = await import('../userDataPersistence');
    await removeUserDataFields([
      FBC_SESSION_KEY,
      FBP_SESSION_KEY,
      FBC_EXPIRY_KEY,
      FBP_EXPIRY_KEY,
      RAW_FBCLID_KEY,
      RAW_FBCLID_EXPIRY_KEY,
    ]);
    debugLog('🗑️ Cleared Facebook tracking data from userDataPersistence');
  } catch (error) {
    debugLog('Could not clear userDataPersistence:', error);
  }
};
